import Dexie, { Table } from "dexie";
import { Task } from "../types";

export interface CategoryRecord {
  id?: number;
  name: string;
  color: string;
}

interface DbSettings {
  key: string;
  value: any;
}

// Define your database schema
export class AppDatabase extends Dexie {
  tasks!: Table<Task, number>;
  settings!: Table<DbSettings>;
  categories!: Table<CategoryRecord, number>;

  constructor() {
    super("focusFlowDB");

    this.version(5).stores({
      tasks: "++id, text, completed, timeSpent, createdAt, category, dueDate, todayOrder, upcomingOrder",
      settings: "key, value",
    }).upgrade(async (tx) => {
      const table = tx.table<Task>("tasks");
      const tasks = await table.toArray();

      const todayTasks = tasks
        .filter((task) => task.isToday && !task.completed)
        .sort(
          (a, b) =>
            (a.todayOrder ?? 0) -
            (b.todayOrder ?? 0)
        );

      const upcomingTasks = tasks
        .filter((task) => !task.isToday && !task.completed)
        .sort(
          (a, b) =>
            (a.upcomingOrder ?? 0) -
            (b.upcomingOrder ?? 0)
        );

      for (let i = 0; i < todayTasks.length; i++) {
        const task = todayTasks[i];
        await table.update(task.id, { todayOrder: i, upcomingOrder: null });
      }

      for (let i = 0; i < upcomingTasks.length; i++) {
        const task = upcomingTasks[i];
        await table.update(task.id, { upcomingOrder: i, todayOrder: null });
      }
    });

    this.version(6).stores({
      tasks: "++id, text, completed, timeSpent, createdAt, category, dueDate, todayOrder, upcomingOrder, completedAt",
      settings: "key, value",
    }).upgrade(async (tx) => {
      const table = tx.table<Task>("tasks");
      const tasks = await table.toArray();

      for (const task of tasks) {
        if (task.completed) {
          await table.update(task.id, {
            completedAt: (task as any).completedAt ?? new Date().toISOString(),
          });
        } else {
          await table.update(task.id, { completedAt: null });
        }
      }
    });

    this.version(7).stores({
      tasks: "++id, text, completed, timeSpent, createdAt, category, dueDate, todayOrder, upcomingOrder, completedAt",
      settings: "key, value",
      categories: "++id, name, color",
    }).upgrade(async (tx) => {
      const tasksTable = tx.table<Task>("tasks");
      const categoriesTable = tx.table<CategoryRecord>("categories");
      const settingsTable = tx.table<DbSettings>("settings");

      const tasks = await tasksTable.toArray();
      const savedCategoriesSetting = await settingsTable.get("savedCategories");
      const savedCategories = Array.isArray(savedCategoriesSetting?.value) ? savedCategoriesSetting!.value as string[] : [];

      const categoryColorMap: Record<string, string> = { ...CATEGORY_COLORS };
      const categoryNames = new Set<string>();

      // Gather from tasks
      tasks.forEach((task) => {
        if (task.category) categoryNames.add(task.category);
      });

      // Gather from savedCategories (legacy)
      savedCategories.forEach((cat) => categoryNames.add(cat));

      // Seed predefined categories
      Object.keys(CATEGORY_COLORS).forEach((cat) => categoryNames.add(cat));

      const names = Array.from(categoryNames);
      for (let i = 0; i < names.length; i += 1) {
        const name = names[i];
        const color = categoryColorMap[name] || "gray";
        // upsert by name (unique by name)
        const existing = await categoriesTable.where("name").equals(name).first();
        if (existing) {
          await categoriesTable.update(existing.id!, { color });
        } else {
          await categoriesTable.add({ name, color });
        }
      }
    });

    this.version(8).stores({
      tasks: "++id, text, completed, timeSpent, createdAt, category, dueDate, todayOrder, upcomingOrder, completedAt",
      settings: "key, value",
      categories: "++id, name, color",
    }).upgrade(async (tx) => {
      const categoriesTable = tx.table<CategoryRecord>("categories");
      const existing = await categoriesTable.toArray();
      const existingByName = new Map(existing.map((r) => [r.name, r]));

      for (const [name, color] of Object.entries(CATEGORY_COLORS)) {
        const rec = existingByName.get(name);
        if (rec) {
          if (rec.color !== color) {
            await categoriesTable.update(rec.id!, { color });
          }
        } else {
          await categoriesTable.add({ name, color });
        }
      }
    });
  }
}

// Create a database instance
const db = new AppDatabase();

export default db;

// Helper functions for common operations
const sortTasks = (tasks: Task[]): Task[] => {
  return tasks.sort((a, b) => {
    const aIsToday = a.isToday && !a.completed;
    const bIsToday = b.isToday && !b.completed;

    if (aIsToday && !bIsToday) return -1;
    if (!aIsToday && bIsToday) return 1;

    if (aIsToday && bIsToday) {
      return (a.todayOrder ?? Number.MAX_SAFE_INTEGER) - (b.todayOrder ?? Number.MAX_SAFE_INTEGER);
    }

    if (!aIsToday && !bIsToday) {
      return (a.upcomingOrder ?? Number.MAX_SAFE_INTEGER) - (b.upcomingOrder ?? Number.MAX_SAFE_INTEGER);
    }

    // Completed or uncategorized tasks: maintain original creation order
    return Date.parse(a.createdAt) - Date.parse(b.createdAt);
  });
};

const reindexAllOrders = async () => {
  const tasks = await db.tasks.toArray();

  const todayTasks = tasks
    .filter((task) => task.isToday && !task.completed)
    .sort((a, b) => (a.todayOrder ?? 0) - (b.todayOrder ?? 0));

  for (let i = 0; i < todayTasks.length; i++) {
    await db.tasks.update(todayTasks[i].id, { todayOrder: i });
  }

  const upcomingTasks = tasks
    .filter((task) => !task.isToday && !task.completed)
    .sort((a, b) => (a.upcomingOrder ?? 0) - (b.upcomingOrder ?? 0));

  for (let i = 0; i < upcomingTasks.length; i++) {
    await db.tasks.update(upcomingTasks[i].id, { upcomingOrder: i });
  }
};

export const taskService = {
  async getAllTasks(): Promise<Task[]> {
    const tasks = await db.tasks.toArray();
    // Convert dueDate strings back to Date objects
    const tasksWithDates = tasks.map(task => ({
      ...task,
      dueDate: task.dueDate ? new Date(task.dueDate) : null,
      todayOrder: task.todayOrder ?? null,
      upcomingOrder: task.upcomingOrder ?? null,
      completedAt: task.completedAt ?? null,
    }));
    return sortTasks(tasksWithDates);
  },

  async addTask(task: Omit<Task, "id">): Promise<number> {
    const tasks = await db.tasks.toArray();

    if (task.isToday) {
      const todayTasks = tasks
        .filter((t) => t.isToday && !t.completed)
        .sort((a, b) => (a.todayOrder ?? 0) - (b.todayOrder ?? 0));

      await Promise.all(
        todayTasks.map((t, index) =>
          db.tasks.update(t.id, { todayOrder: index + 1 })
        )
      );
    } else {
      const upcomingTasks = tasks
        .filter((t) => !t.isToday && !t.completed)
        .sort((a, b) => (a.upcomingOrder ?? 0) - (b.upcomingOrder ?? 0));

      await Promise.all(
        upcomingTasks.map((t, index) =>
          db.tasks.update(t.id, { upcomingOrder: index + 1 })
        )
      );
    }

    const taskWithOrder: any = {
      ...task,
      todayOrder: task.isToday ? 0 : null,
      upcomingOrder: task.isToday ? null : 0,
      completedAt: task.completedAt ?? null,
    };

    if (taskWithOrder.dueDate instanceof Date) {
      taskWithOrder.dueDate = taskWithOrder.dueDate.toISOString();
    }
    if (taskWithOrder.completedAt instanceof Date) {
      taskWithOrder.completedAt = taskWithOrder.completedAt.toISOString();
    }

    const id = await db.tasks.add(taskWithOrder);
    return id;
  },

  async getTask(id: number): Promise<Task | undefined> {
    return await db.tasks.get(id);
  },

  async updateTask(id: number, task: Partial<Task>): Promise<void> {
    const existingTask = await db.tasks.get(id);
    if (!existingTask) {
      throw new Error('Task not found');
    }
    const payload: any = { ...task };
    if (payload.dueDate instanceof Date) {
      payload.dueDate = payload.dueDate.toISOString();
    }
    if (payload.completedAt instanceof Date) {
      payload.completedAt = payload.completedAt.toISOString();
    }
    const updated = await db.tasks.update(id, payload);
    if (updated === 0) {
      throw new Error(`Failed to update task ${id}`);
    }
  },

  async setTaskTodayStatus(taskId: number, isToday: boolean): Promise<void> {
    const task = await db.tasks.get(taskId);
    if (!task) {
      throw new Error('Task not found');
    }

    if (isToday) {
      // Moving into Today: remove from upcoming ordering and append to Today
      const upcomingTasks = await db.tasks
        .filter((t) => !t.isToday && !t.completed && t.id !== taskId)
        .toArray();

      const sortedUpcoming = upcomingTasks.sort(
        (a, b) => (a.upcomingOrder ?? 0) - (b.upcomingOrder ?? 0)
      );
      for (let idx = 0; idx < sortedUpcoming.length; idx++) {
        const t = sortedUpcoming[idx];
        await db.tasks.update(t.id, { upcomingOrder: idx });
      }

      const todayTasks = await db.tasks
        .filter((t) => t.isToday && !t.completed)
        .toArray();

      const newOrder = todayTasks.length;

      await db.tasks.update(taskId, {
        isToday: true,
        todayOrder: newOrder,
        upcomingOrder: null,
      });
    } else {
      // Moving into Upcoming: remove from Today ordering and append to Upcoming
      const todayTasks = await db.tasks
        .filter((t) => t.isToday && !t.completed && t.id !== taskId)
        .toArray();

      const sortedToday = todayTasks.sort(
        (a, b) => (a.todayOrder ?? 0) - (b.todayOrder ?? 0)
      );
      for (let idx = 0; idx < sortedToday.length; idx++) {
        const t = sortedToday[idx];
        await db.tasks.update(t.id, { todayOrder: idx });
      }

      await db.tasks.update(taskId, {
        isToday: false,
        upcomingOrder: 0,
        todayOrder: null,
      });

      const resortedUpcoming = await db.tasks
        .filter((t) => !t.isToday && !t.completed && t.id !== taskId)
        .toArray();

      resortedUpcoming
        .sort((a, b) => (a.upcomingOrder ?? 0) - (b.upcomingOrder ?? 0))
        .forEach(async (t, idx) => {
          await db.tasks.update(t.id, { upcomingOrder: idx + 1 });
        });
    }
  },

  async deleteTask(id: number): Promise<void> {
    await db.tasks.delete(id);

    await reindexAllOrders();
  },

  async deleteTasks(ids: number[]): Promise<void> {
    await db.transaction("rw", db.tasks, async () => {
      await Promise.all(ids.map((id) => db.tasks.delete(id)));
    });

    await reindexAllOrders();
  },

  async toggleTask(taskId: number): Promise<void> {
    const task = await db.tasks.get(taskId);
    if (!task) return;

    const isCompleting = !task.completed;

    if (isCompleting) {
      await db.tasks.update(taskId, { 
        completed: true,
        completedAt: new Date().toISOString(),
        isToday: false,
        todayOrder: null,
        upcomingOrder: null,
      });
    } else {
      await db.tasks.update(taskId, { 
        completed: false,
        completedAt: null,
      });
    }

    await reindexAllOrders();
  },

  async restoreTask(taskId: number): Promise<void> {
    const task = await db.tasks.get(taskId);
    if (!task) return;

    await db.transaction("rw", db.tasks, async () => {
      await db.tasks.update(taskId, {
        completed: false,
        completedAt: null,
      });
    });

    await taskService.setTaskTodayStatus(taskId, false);
  },

  async getAllCategoryRecords(): Promise<CategoryRecord[]> {
    // Prevent concurrent double-seeding by wrapping the seed check in a transaction.
    await db.transaction("rw", db.categories, async () => {
      const count = await db.categories.count();
      if (count === 0) {
        const defaults = Object.entries(CATEGORY_COLORS).map(([name, color]) => ({
          name,
          color,
        }));
        await db.categories.bulkAdd(defaults);
      }
    });

    const records = await db.categories.toArray();

    // Keep in-memory map in sync for consumers that rely on it
    records.forEach((rec) => {
      CATEGORY_COLORS[rec.name] = rec.color || CATEGORY_COLORS[rec.name] || "gray";
    });
    return records.sort((a, b) => a.name.localeCompare(b.name));
  },

  async getAllCategories(): Promise<string[]> {
    const records = await this.getAllCategoryRecords();
    return records.map((r) => r.name);
  },

  getCategoryColor(category: string | undefined): string {
    if (!category) return "gray";
    return CATEGORY_COLORS[category] || "gray";
  },

  async renameCategory(oldName: string, newName: string): Promise<void> {
    // Validate the new name
    const trimmedNewName = newName.trim();
    if (!trimmedNewName || trimmedNewName === oldName) {
      return; // No change needed
    }

    // Check if the new name already exists
    const existing = await db.categories.where("name").equals(trimmedNewName).first();
    if (existing) {
      throw new Error(`Category "${trimmedNewName}" already exists`);
    }

    const oldRecord = await db.categories.where("name").equals(oldName).first();
    const color = oldRecord?.color || CATEGORY_COLORS[oldName] || "gray";

    // Update in-memory color map immediately so UI reflects correct color during rename.
    // Keep the old name mapping around too so any stale references (e.g., unsaved task
    // draft state) still render with the right color until everything refreshes.
    CATEGORY_COLORS[trimmedNewName] = color;
    CATEGORY_COLORS[oldName] = color;

    // Find all tasks with the old category name
    const tasksWithCategory = await db.tasks
      .filter(task => task.category === oldName)
      .toArray();
    
    // Update all tasks to use the new category name
    await db.transaction('rw', db.tasks, async () => {
      for (const task of tasksWithCategory) {
        await db.tasks.update(task.id, { category: trimmedNewName });
      }
    });

    // Update categories table
    if (oldRecord?.id) {
      await db.categories.update(oldRecord.id, { name: trimmedNewName, color });
    } else {
      await db.categories.add({ name: trimmedNewName, color });
    }
  }
};

// Predefined category colors (using Tailwind color names) - 6 core categories
export const CATEGORY_COLORS: Record<string, string> = {
  Home: "green",
  Creative: "yellow",
  Health: "red",
  Study: "purple",
  Social: "gray",
  Work: "blue",
};

// List of available colors for the color pool
export const COLOR_POOL: string[] = [
  "blue",
  "green",
  "red",
  "purple",
  "yellow",
  "gray"
];

// Tailwind-compatible color classes for categories
export const getCategoryColor = (category: string | undefined): string => {
  if (!category) return "gray";
  return CATEGORY_COLORS[category] || "gray";
};

// Cleanup function to remove deprecated categories and rename old categories
export const cleanupDeprecatedCategories = async (): Promise<void> => {
  const deprecatedCategories = ["Finance"];
  const categoryRenames = {
    "Personal": "Home",
    "Other": "Social"
  };
  
  try {
    const tasks = await taskService.getAllTasks();
    
    for (const task of tasks) {
      if (task.category) {
        // Handle deprecated categories (remove them)
        if (deprecatedCategories.includes(task.category)) {
          const updatedTaskData = { ...task, category: undefined };
          await taskService.updateTask(task.id, updatedTaskData);
        }
        // Handle category renames
        else if (task.category in categoryRenames) {
          const newCategoryName = categoryRenames[task.category as keyof typeof categoryRenames];
          const updatedTaskData = { ...task, category: newCategoryName };
          await taskService.updateTask(task.id, updatedTaskData);
        }
      }
    }
  } catch (error) {
    console.error("Error cleaning up deprecated categories:", error);
  }
};

export const settingsService = {
  async getSetting<T>(
    key: string,
    defaultValue: T = null as unknown as T
  ): Promise<T> {
    const record = await db.settings.get(key);
    return record ? record.value : defaultValue;
  },

  async setSetting(key: string, value: any): Promise<void> {
    await db.settings.put({ key, value });
  },

  async getSettings(keys: string[]): Promise<Record<string, any>> {
    const records = await db.settings.where("key").anyOf(keys).toArray();
    const result: Record<string, any> = {};

    records.forEach((record: DbSettings) => {
      result[record.key] = record.value;
    });

    return result;
  },
};
